
import { useState, useEffect } from 'react';
import { Event } from '@/lib/types';
import { fetchMyEvents } from '@/services/eventService';
import { allEvents as mockEvents } from '@/lib/events';
import { useAuth } from '@/contexts/AuthContext';
import { UserRoles } from '@/contexts/auth/enums';

export function useEventLoading(token: string | null) {
  const [allEvents, setAllEvents] = useState<Event[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const { user } = useAuth();

  useEffect(() => {
    // If node dev server is running, use the mock data
    if (import.meta.env.MODE === '-development') {
      setAllEvents(mockEvents);
      setIsLoading(false);
      return;
    }

    const loadEvents = async () => {
      setIsLoading(true);
      try {

        const events = await fetchMyEvents();
        setAllEvents(events);
        setError(null);
      } catch (error) {
        console.error('Failed to fetch events:', error);
        setError(error instanceof Error ? error : new Error('Failed to fetch events'));
      } finally {
        setIsLoading(false);
      }
    };

    loadEvents();
  }, [token, user?.roles]);

  // Find event helper function
  const findEventBySlugOrCode = (slugOrCode: string): Event | undefined => {
    if (!slugOrCode || allEvents.length === 0) return undefined;

    return allEvents.find(e =>
      (e.slug && e.slug === slugOrCode) ||
      (e.code && e.code === slugOrCode) ||
      // Also check for SEO-friendly URLs that might contain the event name
      (e.slug && slugOrCode.startsWith(e.slug + '-'))
    );
  };

  return {
    allEvents,
    isLoading,
    error,
    findEventBySlugOrCode
  };
}
